'use client';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {Alert} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {FacetResult, InternalTypedDocument, search} from '@orama/orama';
import {useSearchParams} from 'next/navigation';
import {ChangeEvent, ComponentProps, useEffect, useMemo, useState} from 'react';
import {useDebouncedCallback} from 'use-debounce';

import FacetBar from '@/components/contentful/activityCatalog/facetBar/facetBar';
import FacetDrawer from '@/components/contentful/activityCatalog/facetDrawer/facetDrawer';
import ActivityCollection from '@/components/csforall/activityCollection/ActivityCollection';
import ActivityCardCollection from '@/modules/activityCatalog/hourOfAi/ActivityCardCollection';
import {
  CATALOG_SEARCH_PARAM,
  parseCatalog,
} from '@/modules/activityCatalog/hourOfAi/paths';
import {createDatabase} from '@/modules/activityCatalog/orama/createDatabase';
import {
  ActivityType,
  OramaActivity,
} from '@/modules/activityCatalog/types/Activity';

import CatalogFilterButtons from './catalogFilterButtons';
import {FACET_CONFIG} from './config/facets';
import HourOfAiSearchField from './hourOfAiSearchField';

interface ActivityCatalogProps {
  /** Every activity, from toOramaActivities. */
  documents: OramaActivity[];
  activities: InternalTypedDocument<OramaActivity>[];
  facets: FacetResult | undefined;
  /** Hour of AI: the facets of each catalog, shown while it is selected. */
  catalogFacets?: Partial<Record<ActivityType, FacetResult>>;
  /**
   * Hour of AI layout: Activity Cards, a "Filters" column with search as its
   * first item, styled like the facets, every facet collapsed, and the
   * catalog filter. Off for CSforAll.
   */
  hourOfAi?: boolean;
}

// The Filters column and, on Hour of AI, the heading above it.
const FILTERS_COLUMN_SX = {
  display: {xs: 'none', sm: 'none', md: 'block'},
  maxWidth: 275,
  flexBasis: 275,
};

// Hour of AI: 32px between the facets and the cards, like the gaps between
// facets.
const HOUR_OF_AI_RESULTS_COLUMN_SX = {pl: {md: 2}};

/**
 * Adds selected values a facet result doesn't have, so a selection made in
 * another catalog stays visible and can be cleared.
 */
function withSelectedValues(
  facets: FacetResult | undefined,
  selectedFacets: Record<string, Set<string>>,
): FacetResult | undefined {
  if (!facets) {
    return facets;
  }

  return Object.fromEntries(
    Object.entries(facets).map(([facet, result]) => {
      const values = {...result.values};
      selectedFacets[facet]?.forEach(value => {
        values[value] ??= 0;
      });
      return [facet, {...result, values}];
    }),
  );
}

const ActivityCatalog = ({
  documents,
  activities,
  facets,
  catalogFacets,
  hourOfAi = false,
}: ActivityCatalogProps) => {
  const allowedFacetSet = new Set(facets ? Object.keys(facets) : []);
  // Add 'excludedOrganizations' to the allowed facet set because it is used as a post-filter
  // (not as a search filter), so we need to recognize it during deserialization but exclude it from search queries.
  allowedFacetSet.add('excludedOrganizations');

  const [results, setResults] =
    useState<InternalTypedDocument<OramaActivity>[]>(activities);
  const [selectedFacets, setSelectedFacets] = useState<
    Record<string, Set<string>>
  >({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  // Hour of AI: the selected catalog; undefined shows all activities.
  const [catalog, setCatalog] = useState<ActivityType | undefined>();
  const [isFacetDrawerOpen, setIsFacetDrawerOpen] = useState<boolean>(false);
  const [isClientLoading, setIsClientLoading] = useState<boolean>(true);

  const searchParams = useSearchParams();
  // Built once, not on every keystroke.
  const db = useMemo(() => createDatabase(documents), [documents]);

  // Populate selected facets and search term from the URL search params on load and when they change.
  useEffect(() => {
    deserializeClientState();
  }, [searchParams]);

  /**
   * Hydrates the client state (search term and selected facets) from the URL search params.
   * This allows for sharing links with specific search terms and facets selected.
   */
  const deserializeClientState = () => {
    // Restore the search term
    const termFromSearchParam = searchParams.get('term') || '';
    setSearchTerm(termFromSearchParam);

    // Restore selected facets
    const facetsFromUrl = deserializeSelectedFacets(searchParams.toString());
    setSelectedFacets(facetsFromUrl);

    // Restore the catalog filter
    const catalogFromUrl = hourOfAi
      ? parseCatalog(searchParams.get(CATALOG_SEARCH_PARAM))
      : undefined;
    setCatalog(catalogFromUrl);

    // Restore the search results based on the restored state
    updateSearchResults(termFromSearchParam, facetsFromUrl, catalogFromUrl);

    setIsClientLoading(false);
  };

  /**
   * Serializes the client state (search term and selected facets) to the URL search params.
   * This allows for sharing links with specific search terms and facets selected.
   * @param searchTerm - The current search term to serialize.
   * @param selectedFacets - The currently selected facets to serialize.
   * @param selectedCatalog - The selected catalog; left out of the URL for all activities.
   */
  const serializeClientState = useDebouncedCallback(
    (
      searchTerm: string,
      selectedFacets: Record<string, Set<string>>,
      selectedCatalog: ActivityType | undefined,
    ) => {
      const params = new URLSearchParams();

      if (selectedCatalog) {
        params.set(CATALOG_SEARCH_PARAM, selectedCatalog);
      }

      /**
       * For each selected facet, add it to the URL search params as a comma-separated list.
       * Each value is individually encoded to handle commas and special characters.
       */
      Object.entries(selectedFacets).forEach(([facet, values]) => {
        if (values.size > 0) {
          // The value may contain commas, so encode each value individually.
          const encodedValues = Array.from(values).map(v =>
            encodeURIComponent(v),
          );
          params.set(facet, encodedValues.join(','));
        }
      });

      // An empty search term is left out, like the catalog filter
      const query = [
        searchTerm && `term=${encodeURIComponent(searchTerm)}`,
        params.toString(),
      ]
        .filter(Boolean)
        .join('&');

      // Update the URL without reloading the page
      // Note: Using pushState instead of router.push to avoid extraneous RSC requests
      // See: https://nextjs.org/docs/app/guides/single-page-applications#shallow-routing-on-the-client
      window.history.pushState(
        null,
        '',
        query ? `?${query}` : window.location.pathname,
      );
    },
    250, // Debounce by 250ms to avoid excessive URL updates while typing
  );

  /**
   * Deserializes the selected facets from the URL search params.
   * Each facet value is expected to be a comma-separated list of values.
   * Each value is individually decoded to handle commas and special characters.
   * @param query - The URL query string to deserialize.
   * @returns An object mapping facet names to sets of selected values.
   */
  const deserializeSelectedFacets = (
    query: string,
  ): Record<string, Set<string>> => {
    const params = new URLSearchParams(query);
    const facets: Record<string, Set<string>> = {};

    params.forEach((value, key) => {
      // Exclude non-allowed facets, such as a malicious user adding arbitrary params.
      if (!allowedFacetSet.has(key)) {
        return;
      }

      // Restore each facet value by splitting on commas and decoding each value.
      const decodedValues = value
        .split(',')
        .map(v => decodeURIComponent(v))
        .filter(Boolean);

      facets[key] = new Set(decodedValues);
    });

    return facets;
  };

  const updateSearchResults = async (
    term: string,
    searchFacets: Record<string, Set<string>>,
    selectedCatalog: ActivityType | undefined,
  ) => {
    if (!db) {
      return;
    }

    /**
     * Convert the selected facets from sets to arrays for Orama query.
     * Example: {ages: Set{'5-7', '8-10'}} becomes {ages: ['5-7', '8-10']}
     */
    const facetFilters = Object.entries(searchFacets).reduce(
      (acc, [facetName, _facetValues]) => {
        if (facetName === 'excludedOrganizations') {
          // Do not include excludedOrganizations in the search filters, it is a post-filter
          return acc;
        }

        const facetValues = _facetValues as Set<string>;
        if (facetValues.size > 0) {
          acc[facetName] = {containsAny: [...facetValues]};
        }
        return acc;
      },
      Object.create({}),
    );

    // Perform the search with the current term and selected facets
    const searchResults = await search(db, {
      term,
      properties: ['title', 'languagesText'],
      where: {
        ...facetFilters,
        ...(selectedCatalog && {catalogs: {containsAny: [selectedCatalog]}}),
      },
      sortBy: {property: 'sortKey', order: 'ASC'},
      // Every match; Orama defaults to 10.
      limit: Math.max(documents.length, 1),
    });

    const nextResults = searchResults.hits
      .filter(hit => {
        // Remove activities from excluded organizations
        const excludedOrganizations = searchFacets['excludedOrganizations'];

        if (excludedOrganizations && excludedOrganizations.size > 0) {
          if (Array.isArray(hit.document.organization)) {
            for (const organization of hit.document.organization) {
              if (excludedOrganizations.has(organization as string)) {
                return false;
              }
            }
          }
        }

        return true;
      })
      .map(hit => hit.document);
    setResults(nextResults);
  };

  /**
   * Updates the search term state and triggers a new search when the user types in the search input.
   * @param e - The change event from the search input.
   */
  const handleSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextSearchTerm = e.target.value;

    // Update the search term state
    setSearchTerm(nextSearchTerm);

    // Update the URL query parameters
    serializeClientState(nextSearchTerm, selectedFacets, catalog);
  };

  /**
   * Handles changes to facet selections when a user checks or unchecks a facet checkbox.
   * Updates the selected facets state, serializes the new state to the URL, and triggers a new search.
   * @param facetName - The name of the facet being changed.
   * @param facetValue - The value of the facet being toggled.
   */
  const handleCheckboxFacetChange = (
    facetName: string,
    facetValue: string | string[],
  ) => {
    if (Array.isArray(facetValue)) {
      // If an array is passed, we only handle single value toggles for checkboxes
      return;
    }

    const newSelectedFacets = {...selectedFacets};

    // Initialize the set for this facet if it doesn't exist yet
    // Note: A set is used to avoid duplicate values
    if (!newSelectedFacets[facetName]) {
      newSelectedFacets[facetName] = new Set<string>();
    }

    // Add or remove the facet value based on whether the checkbox is checked or unchecked
    if (!newSelectedFacets[facetName].has(facetValue)) {
      newSelectedFacets[facetName].add(facetValue);
    } else {
      newSelectedFacets[facetName].delete(facetValue);
    }

    // Update the selected facets state
    setSelectedFacets(newSelectedFacets);
    // Update the URL query parameters
    serializeClientState(searchTerm, newSelectedFacets, catalog);
  };

  /**
   * Handles changes to dropdown facet selections.
   * @param facetName - The name of the facet being changed.
   * @param facetValue - The value of the facet being selected.
   */
  const handleDropdownFacetChange = (
    facetName: string,
    facetValue: string | string[],
  ) => {
    if (!Array.isArray(facetValue)) {
      // For dropdowns, we expect an array of selected values
      return;
    }

    const newSelectedFacets = {...selectedFacets};

    newSelectedFacets[facetName] = new Set<string>(facetValue);

    setSelectedFacets(newSelectedFacets);
    // Update the URL query parameters
    serializeClientState(searchTerm, newSelectedFacets, catalog);
  };

  /**
   * Selects a catalog, or all activities, and clears the search and facets,
   * since the catalogs don't share every facet value. Updates the URL right
   * away rather than after the typing debounce.
   * @param nextCatalog - The catalog to show; undefined for all activities.
   */
  const handleCatalogChange = (nextCatalog: ActivityType | undefined) => {
    if (nextCatalog === catalog) {
      return;
    }

    setCatalog(nextCatalog);
    setSelectedFacets({});
    setSearchTerm('');
    serializeClientState('', {}, nextCatalog);
    serializeClientState.flush();
  };

  const handleFacetChange = (
    facetName: string,
    facetValue: string | string[],
  ) => {
    const facetConfig = FACET_CONFIG[facetName];

    switch (facetConfig?.type) {
      case 'checkbox':
        return handleCheckboxFacetChange(facetName, facetValue);
      case 'dropdown':
        return handleDropdownFacetChange(facetName, facetValue);
      default:
        return;
    }
  };

  const handleClearAll = () => {
    setSelectedFacets({});
    setSearchTerm('');
    serializeClientState('', {}, catalog);
  };

  const toggleFacetDrawer = (isOpen: boolean) => {
    setIsFacetDrawerOpen(isOpen);
  };

  const facetBarProps: ComponentProps<typeof FacetBar> = {
    facets: withSelectedValues(
      (catalog && catalogFacets?.[catalog]) || facets,
      selectedFacets,
    ),
    selectedFacets,
    searchTerm,
    onFacetChange: handleFacetChange,
    onClearAll: handleClearAll,
    onSearchTermChange: handleSearchTermChange,
    hourOfAi,
  };

  return (
    <Box sx={{p: 1}}>
      <FacetDrawer
        {...facetBarProps}
        isOpen={isFacetDrawerOpen}
        onClose={() => toggleFacetDrawer(false)}
      />

      {hourOfAi && (
        // The Filters heading shares a row with the catalog filter, laid out
        // like the columns below, so the first facet lines up with the cards.
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          flexWrap={'nowrap'}
          sx={{mb: 2}}
        >
          <Grid size={3} sx={FILTERS_COLUMN_SX}>
            <Box sx={{px: 2}}>
              <Typography component="h2" variant="h6" sx={{fontWeight: 700}}>
                Filters
              </Typography>
            </Box>
          </Grid>
          <Grid size={9} sx={HOUR_OF_AI_RESULTS_COLUMN_SX}>
            <CatalogFilterButtons
              value={catalog}
              onChange={handleCatalogChange}
            />
          </Grid>
        </Grid>
      )}

      <Grid container justifyContent="center" flexWrap={'nowrap'}>
        <Grid size={3} sx={FILTERS_COLUMN_SX}>
          <FacetBar {...facetBarProps} />
        </Grid>
        <Grid size={9} sx={hourOfAi ? HOUR_OF_AI_RESULTS_COLUMN_SX : undefined}>
          <Box
            sx={{
              // Hour of AI desktop has nothing left in this row.
              display: hourOfAi ? {xs: 'flex', md: 'none'} : 'flex',
              mb: 2,
              gap: 2,
            }}
          >
            {hourOfAi ? (
              // Desktop search sits in the Filters column; phones keep it
              // here beside the Filters button.
              <Box sx={{flexGrow: 1}}>
                <HourOfAiSearchField
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  noMargin
                />
              </Box>
            ) : (
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search..."
                aria-label="Search activities"
                value={searchTerm}
                onChange={handleSearchTermChange}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                }}
              />
            )}
            <Button
              onClick={() => toggleFacetDrawer(true)}
              color={'secondary'}
              variant={'contained'}
              size={'small'}
              sx={{
                display: {xs: 'inline-flex', sm: 'inline-flex', md: 'none'},
                flexShrink: 0,
              }}
            >
              <FilterAltOutlinedIcon /> Filters
            </Button>
          </Box>
          {hourOfAi ? (
            <ActivityCardCollection activities={results} />
          ) : (
            <ActivityCollection activities={results} />
          )}
          {isClientLoading && (
            <Alert severity="info" sx={{justifyContent: 'center', mt: 2}}>
              Loading more activities...
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivityCatalog;
